import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Timer, Play, Pause, RotateCcw, Volume2, VolumeX, Pill, Heart, Zap, ZapOff, Flag, HeartPulse } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";

// ── Metronome using Web Audio API ──
function useMetronome(bpm = 110) {
  const [active, setActive] = useState(false);
  const ctxRef = useRef(null);
  const intervalRef = useRef(null);

  const start = useCallback(() => {
    if (!ctxRef.current) ctxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    const ctx = ctxRef.current;
    const ms = 60000 / bpm;

    const tick = () => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 800;
      gain.gain.value = 0.3;
      osc.start(ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.08);
      osc.stop(ctx.currentTime + 0.08);
    };

    tick();
    intervalRef.current = setInterval(tick, ms);
    setActive(true);
  }, [bpm]);

  const stop = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = null;
    setActive(false);
  }, []);

  const toggle = useCallback(() => {
    active ? stop() : start();
  }, [active, start, stop]);

  useEffect(() => {
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, []);

  return { active, toggle };
}

// ── Cycle Timer (2-min CPR + 10s rhythm check) ──
function CycleTimer({ mode, ratio, onShock, onCycleChange }) {
  const CPR_SECONDS = 120;
  const CHECK_SECONDS = 10;
  const [running, setRunning] = useState(false);
  const [phase, setPhase] = useState("cpr"); // "cpr" | "check"
  const [elapsed, setElapsed] = useState(0);
  const [checkElapsed, setCheckElapsed] = useState(0);
  const [cycle, setCycle] = useState(1);
  const [shocks, setShocks] = useState(0);
  const [log, setLog] = useState([]);
  const startRef = useRef(null);
  const checkStartRef = useRef(null);
  const animRef = useRef(null);
  const totalElapsedRef = useRef(0);

  const playAlert = useCallback(() => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      [0, 0.15, 0.3, 0.45, 0.6].forEach(delay => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain); gain.connect(ctx.destination);
        osc.frequency.value = 1400; gain.gain.value = 0.6;
        osc.start(ctx.currentTime + delay);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delay + 0.1);
        osc.stop(ctx.currentTime + delay + 0.1);
      });
    } catch {}
  }, []);

  const startCheck = useCallback(() => {
    setPhase("check");
    setCheckElapsed(0);
    checkStartRef.current = Date.now();
    playAlert();
  }, [playAlert]);

  const startNextCycle = useCallback(() => {
    setCycle(c => { const next = c + 1; onCycleChange?.(next); return next; });
    setPhase("cpr");
    setCheckElapsed(0);
    checkStartRef.current = null;
    setElapsed(0);
    startRef.current = Date.now();
  }, [onCycleChange]);

  const tick = useCallback(() => {
    if (phase === "cpr" && startRef.current) {
      const e = Math.floor((Date.now() - startRef.current) / 1000);
      totalElapsedRef.current += (e - elapsed > 0 ? e - elapsed : 0);
      setElapsed(e);
      if (e >= CPR_SECONDS) {
        startCheck();
      }
    } else if (phase === "check" && checkStartRef.current) {
      const ce = Math.floor((Date.now() - checkStartRef.current) / 1000);
      setCheckElapsed(ce);
      if (ce >= CHECK_SECONDS) {
        startNextCycle();
      }
    }
    animRef.current = requestAnimationFrame(tick);
  }, [phase, elapsed, startCheck, startNextCycle]);

  const start = () => {
    if (phase === "cpr") {
      startRef.current = Date.now() - elapsed * 1000;
    } else {
      checkStartRef.current = Date.now() - checkElapsed * 1000;
    }
    setRunning(true);
    animRef.current = requestAnimationFrame(tick);
  };

  const pause = () => {
    setRunning(false);
    if (animRef.current) cancelAnimationFrame(animRef.current);
  };

  const reset = () => {
    pause();
    setElapsed(0); setCheckElapsed(0); setCycle(1); setShocks(0); setLog([]);
    setPhase("cpr");
    startRef.current = null; checkStartRef.current = null;
    totalElapsedRef.current = 0;
  };

  const getTimeStr = () => {
    const t = totalElapsedRef.current;
    return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, "0")}`;
  };

  const handleShock = () => {
    const n = shocks + 1; setShocks(n); onShock?.(n);
    const t = getTimeStr();
    setLog(prev => [...prev, { type: "shock", cycle, time: t, num: n }]);
    // Auto-log to intervention tracker (fire-and-forget)
    import("@/utils/interventionLogger").then(({ logIntervention }) => {
      logIntervention({
        type: "intervention",
        name: `Defibrillation shock #${n}`,
        notes: `Code cycle ${cycle} · code-time ${t}`,
      }).catch(() => {});
    });
    startNextCycle();
  };

  const handleNoShock = () => {
    setLog(prev => [...prev, { type: "no-shock", cycle, time: getTimeStr() }]);
    if (phase === "check") startNextCycle();
  };

  useEffect(() => {
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, []);

  // Re-register tick when phase changes
  useEffect(() => {
    if (running) {
      if (animRef.current) cancelAnimationFrame(animRef.current);
      animRef.current = requestAnimationFrame(tick);
    }
  }, [phase, running, tick]);

  const isCheck = phase === "check";
  const remaining = isCheck ? Math.max(CHECK_SECONDS - checkElapsed, 0) : Math.max(CPR_SECONDS - elapsed, 0);
  const pct = isCheck ? (checkElapsed / CHECK_SECONDS) * 100 : (elapsed / CPR_SECONDS) * 100;
  const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
  const ss = String(remaining % 60).padStart(2, "0");
  const isWarning = !isCheck && remaining <= 30;
  const isCritical = !isCheck && remaining <= 10;

  return (
    <Card className={`border-2 transition-colors ${isCheck ? 'border-yellow-500 bg-yellow-500/10' : isCritical ? 'border-red-500 bg-red-500/10' : isWarning ? 'border-amber-500 bg-amber-500/5' : 'border-border'}`} data-testid="cycle-timer">
      <CardContent className="pt-5 pb-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Timer className="w-5 h-5 text-primary" />
            <span className="font-heading font-semibold">{isCheck ? "Rhythm Check" : "CPR Cycle"}</span>
            {shocks > 0 && <Badge className="bg-red-500 text-white text-[10px]">{shocks} shock{shocks !== 1 ? 's' : ''}</Badge>}
            <Badge variant="outline">Cycle {cycle}</Badge>
            {mode === "peds" && <Badge className="bg-violet-500 text-white text-[10px]">{ratio}</Badge>}
          </div>
          <span className="text-xs text-muted-foreground">{isCheck ? "10s rhythm analysis" : "2 min to rhythm check"}</span>
        </div>

        {/* Progress bar */}
        <div className="w-full h-3 bg-secondary rounded-full overflow-hidden mb-3">
          <div
            className={`h-full rounded-full transition-all ${isCheck ? 'bg-yellow-500' : isCritical ? 'bg-red-500' : isWarning ? 'bg-amber-500' : 'bg-primary'}`}
            style={{ width: `${pct}%` }}
          />
        </div>

        {/* Countdown */}
        <div className="text-center mb-3">
          {isCheck && <p className="text-yellow-500 font-bold text-sm mb-1 animate-pulse">RHYTHM CHECK — ANALYZE RHYTHM</p>}
          <span className={`font-mono text-5xl font-bold ${isCheck ? 'text-yellow-500' : isCritical ? 'text-red-500 animate-pulse' : isWarning ? 'text-amber-500' : 'text-foreground'}`} data-testid="cycle-countdown">
            {isCheck ? `00:${String(remaining).padStart(2, "0")}` : `${mm}:${ss}`}
          </span>
          {!isCheck && isCritical && <p className="text-red-500 font-bold text-sm mt-1 animate-pulse">PREPARE FOR RHYTHM CHECK</p>}
        </div>

        {/* Shock / No Shock — always visible during rhythm check, also visible during CPR */}
        {(running || isCheck) && (
          <div className="flex gap-2 justify-center mb-3">
            <Button onClick={handleShock} variant="destructive" className={`gap-2 flex-1 ${isCheck ? 'h-12 text-base' : ''}`} data-testid="shock-btn">
              <Zap className={isCheck ? "w-5 h-5" : "w-4 h-4"} />
              Shock {shocks > 0 && `(${shocks})`}
            </Button>
            <Button onClick={handleNoShock} variant="outline" className={`gap-2 flex-1 ${isCheck ? 'h-12 text-base' : ''}`} data-testid="no-shock-btn">
              <ZapOff className={isCheck ? "w-5 h-5" : "w-4 h-4"} />
              No Shock
            </Button>
          </div>
        )}

        {/* Controls */}
        <div className="flex gap-2 justify-center mb-3">
          <Button onClick={running ? pause : start} className="gap-2" variant={running ? "outline" : "default"} data-testid="cycle-start-pause">
            {running ? <><Pause className="w-4 h-4" />Pause</> : <><Play className="w-4 h-4" />{elapsed > 0 || checkElapsed > 0 ? 'Resume' : 'Start'}</>}
          </Button>
          <Button onClick={reset} variant="ghost" className="gap-2" data-testid="cycle-reset">
            <RotateCcw className="w-4 h-4" />Reset
          </Button>
        </div>

        {/* Event log */}
        {log.length > 0 && (
          <div className="border rounded-md p-2 max-h-28 overflow-auto" data-testid="code-log">
            {log.map((entry, i) => (
              <div key={i} className="text-xs font-mono flex gap-2">
                <span className="text-muted-foreground w-12 shrink-0">{entry.time}</span>
                <span className="text-muted-foreground">C{entry.cycle}</span>
                {entry.type === "shock" ? (
                  <span className="text-red-400 font-semibold">Shock #{entry.num}</span>
                ) : (
                  <span className="text-muted-foreground">No shock</span>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Drug Timer (Epi / Amiodarone) ──
function DrugTimer({ name, icon, onDose }) {
  const [startTime, setStartTime] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [doses, setDoses] = useState(0);
  const animRef = useRef(null);

  const tick = useCallback(() => {
    if (!startTime) return;
    setElapsed(Math.floor((Date.now() - startTime) / 1000));
    animRef.current = requestAnimationFrame(tick);
  }, [startTime]);

  useEffect(() => {
    if (startTime) {
      animRef.current = requestAnimationFrame(tick);
    }
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, [startTime, tick]);

  const administer = () => {
    setStartTime(Date.now());
    setElapsed(0);
    setDoses(prev => { const next = prev + 1; onDose?.(next); return next; });
  };

  const reset = () => {
    if (animRef.current) cancelAnimationFrame(animRef.current);
    setStartTime(null);
    setElapsed(0);
    setDoses(0);
    onDose?.(0);
  };

  const mm = String(Math.floor(elapsed / 60)).padStart(2, "0");
  const ss = String(elapsed % 60).padStart(2, "0");
  const isYellow = elapsed >= 180 && elapsed < 300;
  const isRed = elapsed >= 300;

  return (
    <Card className={`border-2 transition-colors ${isRed ? 'border-red-500 bg-red-500/10' : isYellow ? 'border-amber-500 bg-amber-500/5' : 'border-border'}`} data-testid={`drug-timer-${name.toLowerCase()}`}>
      <CardContent className="pt-5 pb-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            {icon}
            <span className="font-heading font-semibold">{name}</span>
            {doses > 0 && <Badge variant="secondary">Dose {doses}</Badge>}
          </div>
          <div className="flex gap-1">
            <Badge variant="outline" className="bg-amber-500/10 text-amber-500 border-amber-500/30 text-[10px]">3m</Badge>
            <Badge variant="outline" className="bg-red-500/10 text-red-500 border-red-500/30 text-[10px]">5m</Badge>
          </div>
        </div>

        {/* Timer display */}
        <div className="text-center mb-3">
          {startTime ? (
            <>
              <span className={`font-mono text-4xl font-bold ${isRed ? 'text-red-500 animate-pulse' : isYellow ? 'text-amber-500' : 'text-foreground'}`} data-testid={`${name.toLowerCase()}-elapsed`}>
                {mm}:{ss}
              </span>
              {isRed && <p className="text-red-500 font-bold text-sm mt-1 animate-pulse">NEXT DOSE DUE</p>}
              {isYellow && !isRed && <p className="text-amber-500 font-semibold text-sm mt-1">Prepare next dose</p>}
            </>
          ) : (
            <span className="text-2xl font-mono text-muted-foreground">--:--</span>
          )}
        </div>

        {/* Controls */}
        <div className="flex gap-2 justify-center">
          <Button onClick={administer} className="gap-2" data-testid={`${name.toLowerCase()}-administer`}>
            <Pill className="w-4 h-4" />
            {doses === 0 ? "1st Dose Given" : "Next Dose Given"}
          </Button>
          {startTime && (
            <Button onClick={reset} variant="ghost" size="sm" className="gap-1" data-testid={`${name.toLowerCase()}-reset`}>
              <RotateCcw className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ── Code Result Outcome ──
function CodeResult({ stats }) {
  const navigate = useNavigate();
  const [result, setResult] = useState(null); // null | { type, time, elapsed }
  const [totalElapsed, setTotalElapsed] = useState(0);
  const startTimeRef = useRef(stats?.startTime || Date.now());

  useEffect(() => {
    const interval = setInterval(() => {
      setTotalElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const getTimeStr = () => {
    const m = Math.floor(totalElapsed / 60);
    const s = totalElapsed % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  const handleResult = (type) => {
    const time = new Date().toLocaleTimeString();
    const elapsed = getTimeStr();
    setResult({ type, time, elapsed });
    // Auto-log a comprehensive code summary on resolution
    const labelMap = {
      "rosc-field": "ROSC — Field",
      "rosc-hospital": "ROSC — Hospital",
      "tor-field": "TOR — Field",
      "tor-hospital": "TOR — Hospital",
    };
    const summary = [
      `Outcome: ${labelMap[type] || type}`,
      `Wall time: ${time}`,
      `Code duration: ${elapsed}`,
      `Cycles completed: ${Math.max((stats?.cycles ?? 1) - 1, 0)}`,
      `Shocks delivered: ${stats?.shocks ?? 0}`,
      `Epinephrine doses: ${stats?.epi ?? 0}`,
      `Amiodarone doses: ${stats?.amio ?? 0}`,
    ].join(" · ");
    import("@/utils/interventionLogger").then(({ logIntervention }) => {
      logIntervention({
        type: "intervention",
        name: `Code summary — ${labelMap[type] || type}`,
        notes: summary,
      }).catch(() => {});
    });
    if (type === "rosc-field" || type === "rosc-hospital") {
      // Navigate to ROSC protocol after a brief delay
      setTimeout(() => navigate("/protocol/rosc"), 1500);
    }
  };

  if (result) {
    const isROSC = result.type.startsWith("rosc");
    return (
      <Card className={`border-2 ${isROSC ? 'border-green-500 bg-green-500/10' : 'border-muted bg-muted/20'}`} data-testid="code-result-final">
        <CardContent className="pt-5 pb-4 text-center">
          <div className={`text-4xl mb-2 ${isROSC ? 'text-green-400' : 'text-muted-foreground'}`}>
            {isROSC ? <HeartPulse className="w-12 h-12 mx-auto" /> : <Flag className="w-12 h-12 mx-auto" />}
          </div>
          <h3 className="text-lg font-heading font-bold mb-1">
            {result.type === "rosc-field" && "ROSC — Field"}
            {result.type === "rosc-hospital" && "ROSC — Hospital"}
            {result.type === "tor-field" && "TOR — Field"}
            {result.type === "tor-hospital" && "TOR — Hospital"}
          </h3>
          <p className="text-sm text-muted-foreground">Time: {result.time} | Duration: {result.elapsed}</p>
          <p className="text-xs text-muted-foreground mt-1">
            {stats?.shocks ?? 0} shock{(stats?.shocks ?? 0) !== 1 ? 's' : ''} · Epi {stats?.epi ?? 0} · Amio {stats?.amio ?? 0}
          </p>
          <p className="text-xs text-emerald-400 mt-1.5 font-semibold">Logged to Intervention Tracker</p>
          {isROSC && (
            <p className="text-sm text-green-400 font-semibold mt-2 animate-pulse">
              Redirecting to Post-ROSC Care...
            </p>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-border" data-testid="code-result">
      <CardContent className="pt-4 pb-4">
        <div className="flex items-center gap-2 mb-3">
          <Flag className="w-4 h-4 text-muted-foreground" />
          <span className="font-heading font-semibold text-sm">Code Result</span>
          <span className="text-xs text-muted-foreground ml-auto">Logs full summary on selection</span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Button
            onClick={() => handleResult("rosc-field")}
            className="gap-2 bg-green-600 hover:bg-green-700 text-white h-12"
            data-testid="result-rosc-field"
          >
            <HeartPulse className="w-4 h-4" />
            ROSC — Field
          </Button>
          <Button
            onClick={() => handleResult("rosc-hospital")}
            className="gap-2 bg-green-700 hover:bg-green-800 text-white h-12"
            data-testid="result-rosc-hospital"
          >
            <HeartPulse className="w-4 h-4" />
            ROSC — Hospital
          </Button>
          <Button
            onClick={() => handleResult("tor-field")}
            variant="outline"
            className="gap-2 h-12 border-red-500/30 text-red-400 hover:bg-red-500/10"
            data-testid="result-tor-field"
          >
            <Flag className="w-4 h-4" />
            TOR — Field
          </Button>
          <Button
            onClick={() => handleResult("tor-hospital")}
            variant="outline"
            className="gap-2 h-12 border-red-500/30 text-red-400 hover:bg-red-500/10"
            data-testid="result-tor-hospital"
          >
            <Flag className="w-4 h-4" />
            TOR — Hospital
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Main Page ──
export default function CodeTimer() {
  const [mode, setMode] = useState("adult"); // "adult" | "peds"
  const [ratio, setRatio] = useState("30:2"); // "30:2" | "15:2"
  const bpm = 110;
  const metronome = useMetronome(bpm);

  // Aggregate code stats for comprehensive resolution log
  const [stats, setStats] = useState({ shocks: 0, cycles: 1, epi: 0, amio: 0, startTime: Date.now() });

  return (
    <div className="space-y-4 max-w-2xl mx-auto" data-testid="code-timer-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
            <Heart className="w-6 h-6 text-red-500" />
            Code Timer
          </h1>
          <p className="text-muted-foreground text-sm">Cardiac arrest management</p>
        </div>

        {/* Metronome toggle */}
        <Button
          onClick={metronome.toggle}
          variant={metronome.active ? "default" : "outline"}
          className={`gap-2 ${metronome.active ? 'bg-primary animate-pulse' : ''}`}
          data-testid="metronome-toggle"
        >
          {metronome.active ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
          {bpm} BPM
          {metronome.active && <Badge variant="secondary" className="text-[10px] ml-1">ON</Badge>}
        </Button>
      </div>

      {/* Mode Selector: Adult / Pediatric */}
      <div className="flex gap-2" data-testid="mode-selector">
        <Button
          variant={mode === "adult" ? "default" : "outline"}
          className={`flex-1 gap-2 h-11 ${mode === "adult" ? "" : ""}`}
          onClick={() => { setMode("adult"); setRatio("30:2"); }}
          data-testid="mode-adult"
        >
          <Heart className="w-4 h-4" />
          Adult
          {mode === "adult" && <Badge variant="secondary" className="text-[10px]">30:2</Badge>}
        </Button>
        <Button
          variant={mode === "peds" ? "default" : "outline"}
          className={`flex-1 gap-2 h-11 ${mode === "peds" ? "bg-violet-600 hover:bg-violet-700" : ""}`}
          onClick={() => setMode("peds")}
          data-testid="mode-peds"
        >
          <Heart className="w-4 h-4" />
          Pediatric
          {mode === "peds" && <Badge variant="secondary" className="text-[10px]">{ratio}</Badge>}
        </Button>
      </div>

      {/* Pediatric Ratio Selector */}
      {mode === "peds" && (
        <div className="flex gap-2" data-testid="ratio-selector">
          <Button
            variant={ratio === "15:2" ? "default" : "outline"}
            className={`flex-1 h-10 ${ratio === "15:2" ? "bg-violet-600 hover:bg-violet-700" : ""}`}
            onClick={() => setRatio("15:2")}
            data-testid="ratio-15-2"
          >
            <span className="font-mono font-bold">15:2</span>
            <span className="text-xs ml-1.5 opacity-80">2 Rescuers</span>
          </Button>
          <Button
            variant={ratio === "30:2" ? "default" : "outline"}
            className={`flex-1 h-10 ${ratio === "30:2" ? "bg-violet-600 hover:bg-violet-700" : ""}`}
            onClick={() => setRatio("30:2")}
            data-testid="ratio-30-2"
          >
            <span className="font-mono font-bold">30:2</span>
            <span className="text-xs ml-1.5 opacity-80">1 Rescuer</span>
          </Button>
        </div>
      )}

      {/* CPR Cycle Timer */}
      <CycleTimer
        mode={mode}
        ratio={ratio}
        onShock={(n) => setStats((s) => ({ ...s, shocks: n }))}
        onCycleChange={(n) => setStats((s) => ({ ...s, cycles: n }))}
      />

      {/* Drug Timers */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <DrugTimer name="Epinephrine" icon={<Pill className="w-5 h-5 text-green-500" />} onDose={(n) => setStats((s) => ({ ...s, epi: n }))} />
        <DrugTimer name="Amiodarone" icon={<Pill className="w-5 h-5 text-blue-500" />} onDose={(n) => setStats((s) => ({ ...s, amio: n }))} />
      </div>

      {/* Code Result */}
      <CodeResult stats={stats} />

      <p className="text-[10px] text-muted-foreground text-center">
        {mode === "adult"
          ? "CPR: 2-min cycles, 30:2 ratio, minimize interruptions. Epi q3-5min. Amiodarone after 3rd shock."
          : `Pediatric CPR: 2-min cycles, ${ratio} ratio. Epi 0.01mg/kg q3-5min. Amiodarone 5mg/kg after 3rd shock.`
        }
      </p>
    </div>
  );
}
